import { updateVendorProductionCapacity } from '@api/vendors';
import { Button, Chip, Group, NumberInput, Stack, Text } from '@mantine/core';
import { useEffect, useState, type FC } from 'react';

import classes from './VendorProductionCapacity.module.css';

const WEEKDAYS: { value: string; label: string }[] = [
    { value: '1', label: 'Mon' },
    { value: '2', label: 'Tue' },
    { value: '3', label: 'Wed' },
    { value: '4', label: 'Thu' },
    { value: '5', label: 'Fri' },
    { value: '6', label: 'Sat' },
    { value: '0', label: 'Sun' },
];
const ALL_DAYS = WEEKDAYS.map((d) => d.value);

type Props = {
    vendorId: string;
    dailyProductionCapacity: number | null;
    workingDays: number[] | null;
    onSaved: (next: { dailyProductionCapacity: number | null; workingDays: number[] | null }) => void;
};

/**
 * A manufacturer's production capacity — what replenishment planning caps its runs at.
 * Blank capacity means unconstrained, which is how every factory plans until this is set.
 */
const VendorProductionCapacity: FC<Props> = ({ vendorId, dailyProductionCapacity, workingDays, onSaved }) => {
    const [capacity, setCapacity] = useState<number | ''>(dailyProductionCapacity ?? '');
    const [days, setDays] = useState<string[]>(workingDays?.length ? workingDays.map(String) : ALL_DAYS);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setCapacity(dailyProductionCapacity ?? '');
        setDays(workingDays?.length ? workingDays.map(String) : ALL_DAYS);
        setError(null);
    }, [vendorId, dailyProductionCapacity, workingDays]);

    const nextCapacity = capacity === '' ? null : capacity;
    const nextDays = days.length === ALL_DAYS.length ? null : days.map(Number).sort((a, b) => a - b);
    const savedDays = workingDays?.length ? [...workingDays].sort((a, b) => a - b) : null;
    const dirty =
        nextCapacity !== (dailyProductionCapacity ?? null) || JSON.stringify(nextDays) !== JSON.stringify(savedDays);

    const save = async () => {
        if (days.length === 0) {
            setError('Pick at least one working day.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const next = { dailyProductionCapacity: nextCapacity, workingDays: nextDays };
            await updateVendorProductionCapacity(vendorId, next);
            onSaved(next);
        } catch {
            setError('Could not save the capacity. Try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack gap="xs">
            <Group className={classes.row} gap="lg">
                <NumberInput
                    label="Units per working day"
                    placeholder="No limit"
                    value={capacity}
                    onChange={(v) => setCapacity(typeof v === 'number' ? v : '')}
                    min={0}
                    allowDecimal={false}
                    thousandSeparator=","
                    w={200}
                />
                <Stack gap={4}>
                    <Text size="sm" fw={500}>
                        Working days
                    </Text>
                    <Chip.Group multiple value={days} onChange={setDays}>
                        <Group gap={6}>
                            {WEEKDAYS.map((d) => (
                                <Chip key={d.value} value={d.value} size="xs">
                                    {d.label}
                                </Chip>
                            ))}
                        </Group>
                    </Chip.Group>
                </Stack>
                <Button size="xs" onClick={() => void save()} loading={saving} disabled={!dirty}>
                    Save
                </Button>
            </Group>
            <Text size="xs" c={error ? 'red' : 'dimmed'} className={classes.hint}>
                {error ??
                    'Replenishment plans cap this factory at this rate. Production over the cap is built earlier, moved to another capable manufacturer, or pushed later. Leave blank for no limit.'}
            </Text>
        </Stack>
    );
};

export default VendorProductionCapacity;
